import Quill from 'quill'

import { Nullable } from '@shared/models/types/primary'

const Module = Quill.import('core/module')
const BlockEmbed = Quill.import('blots/block/embed')
const Link = Quill.import('formats/link')

export class ImageBlot extends BlockEmbed {
  static blotName = 'image'
  static tagName = ['figure', 'image']

  // eslint-disable-next-line
  static create(value: any) {
    const node = super.create()
    const img = window.document.createElement('img')
    if (value.alt || value.caption) {
      img.setAttribute('alt', value.alt || value.caption)
    }
    if (value.src || typeof value === 'string') {
      img.setAttribute('src', value.src || value)
    }

    node.appendChild(img)
    if (value.caption) {
      const caption = window.document.createElement('figcaption')
      caption.innerHTML = value.caption
      node.appendChild(caption)
    }
    node.className = 'ql-card-editable ql-card-figure'
    return node
  }

  // eslint-disable-next-line
  constructor(node: any) {
    super(node)
    node.__onSelect = () => {
      if (!node.querySelector('input')) {
        let caption = node.querySelector('figcaption')
        const captionInput = window.document.createElement('input')
        captionInput.placeholder = 'Type your caption...'
        if (caption) {
          captionInput.value = caption.innerText
          caption.innerHTML = ''
          caption.appendChild(captionInput)
        } else {
          caption = window.document.createElement('figcaption')
          caption.appendChild(captionInput)
          node.appendChild(caption)
        }
        captionInput.addEventListener('blur', () => {
          const value = captionInput.value
          if (!value || value === '') {
            caption.remove()
          } else {
            captionInput.remove()
            caption.innerText = value
          }
        })
        captionInput.focus()
      }
    }
  }

  // eslint-disable-next-line
  static value(node: any) {
    const img = node.querySelector('img')
    const figcaption = node.querySelector('figcaption')
    if (!img) {
      return false
    }
    return {
      alt: img.getAttribute('alt'),
      src: img.getAttribute('src'),
      caption: figcaption ? figcaption.innerText : null,
    }
  }
}

export class CardEditableModule extends Module {
  // eslint-disable-next-line
  constructor(quill: any, options: any) {
    super(quill, options)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listener = (e: any): any => {
      if (!document.body.contains(quill.root)) {
        return document.body.removeEventListener('click', listener)
      }
      const elm = e.target.closest('.ql-card-editable')
      const deselectCard = () => {
        if (elm.__onDeselect) {
          elm.__onDeselect(quill)
        } else {
          quill.setSelection(
            quill.getIndex(elm.__blot.blot) + 1,
            0,
            Quill.sources.USER
          )
        }
      }
      if (elm && elm.__blot && elm.__onSelect) {
        quill.disable()
        elm.__onSelect(quill)
        // eslint-disable-next-line
        const handleKeyPress = (e: any) => {
          if (e.keyCode === 27 || e.keyCode === 13) {
            window.removeEventListener('keypress', handleKeyPress)
            quill.enable(true)
            deselectCard()
          }
        }
        // eslint-disable-next-line
        const handleClick = (e: any) => {
          if (e.which === 1 && !elm.contains(e.target)) {
            window.removeEventListener('click', handleClick)
            quill.enable(true)
            deselectCard()
          }
        }
        window.addEventListener('keypress', handleKeyPress)
        window.addEventListener('click', handleClick)
      }
    }
    quill.emitter.listenDOM('click', document.body, listener)
  }
}

class CustomLink extends Link {
  static create(url: string) {
    const node = super.create()

    const formattedUrl = this.sanitize(url)

    node.setAttribute('href', formattedUrl)
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', node.rel)
    node.setAttribute('translate', 'no')
    node.setAttribute('class', 'quill-link')

    return node
  }

  static sanitize(url: string) {
    const value = super.sanitize(url)
    if (value) {
      for (let i = 0; i < this.PROTOCOL_WHITELIST.length; i++) {
        if (value.startsWith(this.PROTOCOL_WHITELIST[i])) {
          return value
        }
      }

      return `https://${value}`
    }
    return value
  }
}

export const extendQuillEditor = () => {
  Quill.register(
    {
      'formats/image': ImageBlot,
      'modules/cardEditable': CardEditableModule,
      'formats/link': CustomLink,
    },
    true
  )
}

const setupGlobalToolbarListeners = () => {
  let expanded = false // Keeps track of whether the editor has been expanded or not.

  // Function to check if the Quill tooltip is currently visible (not hidden).
  const isTooltipVisible = (): boolean => {
    const tooltip = document.querySelector('.ql-tooltip')
    return tooltip ? !tooltip.classList.contains('ql-hidden') : false
  }

  const getQuillTargets = (e: MouseEvent) => {
    const target = e.target as HTMLElement

    return {
      linkToolbar: target.closest('.ql-link'), // Check if the target is the link button in the toolbar.
      linkEditor:
        target.closest('.quill-link') || target.querySelector('.quill-link'), // Check if the target is a link in the editor.
      toolbarActionButton: target.closest('.ql-action'), // Check if the target is an "action" button in the tooltip (e.g., edit button).
      toolbarRemoveButton: target.closest('.ql-remove'), // Check if the target is the "remove" button in the tooltip.
      tooltipClick: target.closest('.ql-tooltip'), // Check if the target is any part of the tooltip.
    }
  }

  const adjustEditorHeight = (expand: boolean) => {
    const editor: Nullable<HTMLElement> = document.querySelector('.ql-editor')
    if (!editor) {
      return
    }

    // If expanding and it's not already expanded, increase the editor's height.
    if (expand && !expanded) {
      const originHeight = editor.offsetHeight
      editor.style.height = `${originHeight + 50}px`
      expanded = true
    }
    // If collapsing and it's currently expanded, reset the height to "auto".
    else if (!expand && expanded) {
      editor.style.height = '100%'
      expanded = false
    }
  }

  document.body.addEventListener('click', (e) => {
    const tooltip = document.querySelector('.ql-tooltip')
    const isTooltipVisibleNow = isTooltipVisible()

    const {
      linkToolbar,
      linkEditor,
      toolbarActionButton,
      toolbarRemoveButton,
      tooltipClick,
    } = getQuillTargets(e)

    const isEdit = toolbarActionButton && isTooltipVisibleNow

    const isSave = toolbarActionButton && !isTooltipVisibleNow

    // Check if we need to adjust the editor height (when link toolbar or link in editor is clicked, or when editing).
    const needToAdjustHeight =
      linkToolbar || (linkEditor && isTooltipVisibleNow) || isEdit

    // Check if we need to decrease/reset the height (when clicking outside the tooltip or saving/removing).
    const needToDecreaseHeight =
      (!tooltipClick || isSave || toolbarRemoveButton) && tooltip

    if (needToAdjustHeight) {
      adjustEditorHeight(true)
    } else if (needToDecreaseHeight) {
      adjustEditorHeight(false)
    }
  })
}

setupGlobalToolbarListeners()
